import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CalendarOff, Phone, Mail } from "lucide-react";

const STORAGE_KEY = "vespa-closure-2026-06-25-dismissed";
// Pop-up wird ab diesem Datum automatisch nicht mehr angezeigt
const CLOSURE_END = new Date("2026-07-02T00:00:00");

export default function ClosureNotice() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Auto-hide once the closure period is over
    if (new Date() >= CLOSURE_END) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) setOpen(true);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
          data-testid="closure-notice"
        >
          {/* Backdrop */}
          <button
            type="button"
            onClick={close}
            aria-label="Schliessen"
            className="absolute inset-0 bg-[var(--anthracite)]/70 backdrop-blur-sm"
            data-testid="closure-backdrop"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-[var(--cream)] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="closure-title"
          >
            <div className="tricolore absolute top-0 left-0 right-0 h-[3px]" />

            <button
              type="button"
              onClick={close}
              aria-label="Schliessen"
              className="absolute top-4 right-4 w-9 h-9 grid place-items-center text-[var(--anthracite)] hover:bg-[var(--cream-alt)] transition-colors"
              data-testid="closure-close-button"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <div className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 grid place-items-center bg-[var(--sage-dark)] text-[var(--cream)]">
                  <CalendarOff size={18} strokeWidth={1.4} />
                </span>
                <span className="overline">Wichtige Mitteilung</span>
              </div>

              <h2
                id="closure-title"
                className="h-display text-4xl md:text-5xl text-[var(--anthracite)] leading-[1]"
              >
                Casa Markus{" "}
                <span className="italic-accent text-[var(--sage-dark)]">geschlossen.</span>
              </h2>

              <div className="mt-8 border-l-2 border-[var(--sage-dark)] pl-5">
                <p className="overline text-[10px]">Wieder da</p>
                <p className="font-serif text-2xl md:text-3xl text-[var(--anthracite)] mt-2 leading-snug">
                  Ab <span className="italic-accent">Do</span> 02. Juli 2026
                </p>
              </div>

              <p className="mt-8 text-sm md:text-base text-[var(--muted-fg)] leading-relaxed">
                Unser Geschäft bleibt während dieser Zeit geschlossen.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                <a
                  href="tel:+41614218833"
                  className="btn-ghost !py-3 justify-center"
                  data-testid="closure-call"
                >
                  <Phone size={14} strokeWidth={1.5} />
                  061 421 88 33
                </a>
                <a
                  href="mailto:original@vespa-basel.ch"
                  className="btn-primary !py-3 justify-center"
                  data-testid="closure-email"
                >
                  <Mail size={14} strokeWidth={1.5} />
                  Nachricht senden
                </a>
              </div>

              <button
                type="button"
                onClick={close}
                className="mt-6 w-full text-center text-[11px] uppercase tracking-[0.22em] text-[var(--muted-fg)] hover:text-[var(--anthracite)] transition-colors py-2"
                data-testid="closure-acknowledge"
              >
                Verstanden — weiterstöbern
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
